import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Database, Film, HardDrive, Users, MessageSquare,
  RefreshCw, Loader2, Play, Clock, CheckCircle2, Brain,
  ChevronDown, ChevronUp, Sparkles, Download, BarChart3, TrendingUp, Activity,
  AlertTriangle, Bell, BellOff, Settings2, Save, ShieldAlert, Mail, Send, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { BACKEND_URL, API } from '@/lib/backendUrl';


function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatTimeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-[#F7931A]' }) {
  return (
    <Card className="bg-white/[0.03] border-white/5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/5`}>
            <Icon className={`w-4 h-4 ${color}`} />
          </div>
          <div>
            <p className="text-xl font-bold text-white heading-font">{value}</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
            {sub && <p className="text-[9px] text-gray-600 mt-0.5">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function HotelMemoryCard({ hotel, onCompile, compiling, hasAlert }) {
  const [expanded, setExpanded] = useState(false);
  const hasMP4 = hotel.mp4_files > 0;
  const hasPending = hotel.pending_compilation > 0;

  return (
    <Card className={`bg-white/[0.03] overflow-hidden ${hasAlert ? 'border-amber-500/30 shadow-[0_0_12px_-3px_rgba(245,158,11,0.15)]' : 'border-white/5'}`} data-testid={`hotel-memory-${hotel.hotel_id}`}>
      <CardContent className="p-0">
        {/* Alert indicator */}
        {hasAlert && (
          <div className="px-4 py-1.5 bg-amber-500/[0.06] border-b border-amber-500/10 flex items-center gap-1.5" data-testid={`alert-indicator-${hotel.hotel_id}`}>
            <AlertTriangle className="w-3 h-3 text-amber-400" />
            <span className="text-[9px] text-amber-400 font-medium">Threshold breached</span>
          </div>
        )}
        {/* Header */}
        <div className="p-4 flex items-start justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              hasMP4 ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-white/[0.04] border border-white/8'
            }`}>
              <Film className={`w-5 h-5 ${hasMP4 ? 'text-purple-400' : 'text-gray-500'}`} />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm text-gray-100 truncate heading-font">{hotel.hotel_name}</h3>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                {hasMP4 && (
                  <Badge variant="outline" className="text-[9px] border-purple-500/20 text-purple-400 bg-purple-500/5">
                    MP4 Active
                  </Badge>
                )}
                <span className="text-[10px] text-gray-600 mono">{formatBytes(hotel.mp4_size_bytes)}</span>
                {hasPending && (
                  <Badge variant="outline" className="text-[9px] border-amber-500/20 text-amber-400 bg-amber-500/5">
                    {hotel.pending_compilation} pending
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {hasMP4 && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs gap-1.5 h-8 border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${API}/api/metaverse/bitsy/download/${hotel.hotel_id}`;
                  link.download = `${hotel.hotel_id}_memory.mp4`;
                  link.click();
                  toast.success('Downloading MP4 memory file...');
                }}
                data-testid={`download-btn-${hotel.hotel_id}`}
              >
                <Download className="w-3 h-3" />
                MP4
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className={`text-xs gap-1.5 h-8 ${
                hasPending
                  ? 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10'
                  : 'border-white/10 text-gray-400 hover:bg-white/5'
              }`}
              onClick={() => onCompile(hotel.hotel_id)}
              disabled={compiling}
              data-testid={`compile-btn-${hotel.hotel_id}`}
            >
              {compiling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              Compile
            </Button>
          </div>
        </div>

        {/* Metrics row */}
        <div className="px-4 pb-3 grid grid-cols-4 gap-3">
          {[
            { label: 'Memories', value: hotel.total_memories, icon: Brain },
            { label: 'Compiled', value: hotel.compiled_memories, icon: CheckCircle2 },
            { label: 'Guests', value: hotel.unique_guests, icon: Users },
            { label: 'Sessions', value: hotel.total_sessions, icon: MessageSquare },
          ].map(m => (
            <div key={m.label} className="bg-white/[0.02] rounded-lg p-2 text-center border border-white/[0.03]">
              <m.icon className="w-3 h-3 text-gray-500 mx-auto mb-0.5" />
              <p className="text-sm font-bold text-white">{m.value}</p>
              <p className="text-[8px] text-gray-600 uppercase">{m.label}</p>
            </div>
          ))}
        </div>

        {/* Storage bar */}
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between text-[9px] text-gray-600 mb-1">
            <span>Compilation progress</span>
            <span>{hotel.total_memories > 0 ? Math.round((hotel.compiled_memories / hotel.total_memories) * 100) : 0}%</span>
          </div>
          <div className="w-full h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-purple-500 to-[#F7931A] transition-all duration-700"
              style={{ width: `${hotel.total_memories > 0 ? (hotel.compiled_memories / hotel.total_memories) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Expandable recent memories */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-4 py-2 flex items-center justify-between text-[10px] text-gray-500 hover:text-gray-300 hover:bg-white/[0.02] transition-colors border-t border-white/[0.03]"
          data-testid={`expand-btn-${hotel.hotel_id}`}
        >
          <span>Recent Memories ({hotel.recent_memories?.length || 0})</span>
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>

        {expanded && (
          <div className="px-4 pb-3 space-y-1.5 max-h-48 overflow-y-auto" data-testid={`memories-list-${hotel.hotel_id}`}>
            {(hotel.recent_memories || []).map((mem, i) => (
              <div key={i} className="flex items-start gap-2 py-1.5 border-b border-white/[0.02] last:border-0">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${mem.compiled ? 'bg-purple-400' : 'bg-amber-400'}`} />
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-300 leading-relaxed truncate">{mem.content}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[8px] text-gray-600 mono">{mem.guest_id?.slice(0, 16)}</span>
                    <span className="text-[8px] text-gray-600">{formatTimeAgo(mem.timestamp)}</span>
                    <Badge variant="outline" className={`text-[7px] h-3 ${
                      mem.compiled ? 'border-purple-500/20 text-purple-400' : 'border-amber-500/20 text-amber-400'
                    }`}>
                      {mem.compiled ? 'compiled' : 'buffered'}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
            {(!hotel.recent_memories || hotel.recent_memories.length === 0) && (
              <p className="text-[10px] text-gray-600 py-2 text-center">No memories stored yet</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const CHART_COLORS = ['#a855f7', '#F7931A', '#22c55e', '#3b82f6', '#06b6d4', '#f59e0b'];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0a0f1a] border border-white/10 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-[10px] text-gray-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color }}>
          {p.name}: <span className="font-bold text-white">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

function MemoryTrendChart({ data }) {
  if (!data?.length) return null;
  return (
    <Card className="bg-white/[0.03] border-white/5" data-testid="memory-trend-chart">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-semibold heading-font text-gray-200">Memory Activity (30 days)</h3>
        </div>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradCompiled" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradBuffered" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F7931A" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F7931A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6b7280' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="compiled" name="Compiled" stroke="#a855f7" fill="url(#gradCompiled)" strokeWidth={2} />
              <Area type="monotone" dataKey="buffered" name="Buffered" stroke="#F7931A" fill="url(#gradBuffered)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center gap-4 mt-3 justify-center">
          <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-purple-500" /><span className="text-[9px] text-gray-500">Compiled</span></div>
          <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#F7931A]" /><span className="text-[9px] text-gray-500">Buffered</span></div>
        </div>
      </CardContent>
    </Card>
  );
}

function HotelVolumeChart({ data }) {
  if (!data?.length) return null;
  return (
    <Card className="bg-white/[0.03] border-white/5" data-testid="hotel-volume-chart">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-4 h-4 text-[#F7931A]" />
          <h3 className="text-sm font-semibold heading-font text-gray-200">Memory Volume by Hotel</h3>
        </div>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="compiled" name="Compiled" stackId="a" radius={[0, 0, 0, 0]}>
                {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.8} />)}
              </Bar>
              <Bar dataKey="buffered" name="Buffered" stackId="a" radius={[4, 4, 0, 0]}>
                {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.3} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function CompilationTimeline({ data }) {
  if (!data?.length) return null;
  return (
    <Card className="bg-white/[0.03] border-white/5" data-testid="compilation-timeline">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-semibold heading-font text-gray-200">Compilation History</h3>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {data.map((entry, i) => (
            <div key={i} className="flex items-center gap-3 py-1.5 border-b border-white/[0.03] last:border-0">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
              <span className="text-[10px] text-gray-400 mono w-20 shrink-0">{entry.date}</span>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[8px] h-4 border-purple-500/20 text-purple-400">
                  {entry.compiled} compiled
                </Badge>
                <Badge variant="outline" className="text-[8px] h-4 border-blue-500/20 text-blue-400">
                  {entry.hotels} hotel{entry.hotels !== 1 ? 's' : ''}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function AnalyticsSummaryCards({ summary }) {
  if (!summary) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="analytics-summary">
      <StatCard icon={Brain} label="Total Memories" value={summary.total_memories} color="text-purple-400" />
      <StatCard icon={CheckCircle2} label="Compiled" value={summary.total_compiled} color="text-emerald-400" />
      <StatCard icon={Clock} label="Buffered" value={summary.total_buffered} color="text-amber-400" />
      <StatCard icon={TrendingUp} label="Compile Rate" value={`${summary.compilation_rate}%`} color="text-cyan-400" />
      <StatCard icon={HardDrive} label="Total Storage" value={formatBytes(summary.total_storage_bytes)} color="text-[#F7931A]" />
    </div>
  );
}

function ActiveAlerts({ alerts }) {
  if (!alerts?.length) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-500/[0.04] border border-emerald-500/10" data-testid="alerts-clear">
        <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
        <p className="text-[11px] text-emerald-400">All systems healthy — no alerts triggered</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="active-alerts">
      {alerts.map((a, i) => (
        <div
          key={i}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border ${
            a.severity === 'critical'
              ? 'bg-red-500/[0.05] border-red-500/20'
              : 'bg-amber-500/[0.05] border-amber-500/20'
          }`}
          data-testid={`alert-${a.hotel_id}-${a.type}`}
        >
          {a.severity === 'critical'
            ? <ShieldAlert className="w-4 h-4 text-red-400 shrink-0" />
            : <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          }
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-gray-200">{a.hotel_name}</span>
              <Badge variant="outline" className={`text-[7px] h-3.5 ${
                a.severity === 'critical'
                  ? 'border-red-500/30 text-red-400 bg-red-500/10'
                  : 'border-amber-500/30 text-amber-400 bg-amber-500/10'
              }`}>
                {a.severity}
              </Badge>
              <Badge variant="outline" className="text-[7px] h-3.5 border-white/10 text-gray-500">
                {a.type.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-[10px] text-gray-400 mt-0.5">{a.message}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function AlertConfigPanel({ config, onSave, saving, onTest, testing }) {
  const [local, setLocal] = useState(config || {});

  useEffect(() => { if (config) setLocal(config); }, [config]);

  const handleSave = () => {
    onSave({
      buffer_threshold: local.buffer_threshold,
      storage_limit_mb: local.storage_limit_mb,
      compile_failure_alert: local.compile_failure_alert,
      notifications_enabled: local.notifications_enabled,
      email_enabled: local.email_enabled,
      alert_email: local.alert_email,
      telegram_enabled: local.telegram_enabled,
      alert_telegram_chat_id: local.alert_telegram_chat_id,
      alert_telegram_bot_token: local.alert_telegram_bot_token,
      cooldown_minutes: local.cooldown_minutes,
    });
  };

  const changed = config && JSON.stringify(local) !== JSON.stringify(config);

  return (
    <Card className="bg-white/[0.03] border-white/5" data-testid="alert-config-panel">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold heading-font text-gray-200">Alert Configuration</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-xs gap-1.5 h-7 border-white/10 text-gray-400 hover:bg-white/5"
              onClick={onTest}
              disabled={testing || (!local.email_enabled && !local.telegram_enabled)}
              data-testid="test-alerts-btn"
            >
              {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
              Test
            </Button>
            <Button
              size="sm"
              className={`text-xs gap-1.5 h-7 ${
                changed ? 'bg-[#F7931A] hover:bg-[#e8841a] text-black' : 'bg-white/5 text-gray-500 cursor-default'
              }`}
              onClick={handleSave}
              disabled={!changed || saving}
              data-testid="save-config-btn"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save
            </Button>
          </div>
        </div>

        {/* Thresholds row */}
        <div>
          <p className="text-[9px] text-gray-600 uppercase tracking-wider mb-2">Thresholds</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-[9px] text-gray-500 block mb-1">Buffer Limit</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number" min={1}
                  value={local.buffer_threshold || 50}
                  onChange={e => setLocal(p => ({ ...p, buffer_threshold: parseInt(e.target.value) || 1 }))}
                  className="w-full h-7 bg-white/[0.04] border border-white/8 rounded-lg px-2 text-[11px] text-white focus:border-purple-500/40 focus:outline-none"
                  data-testid="config-buffer-threshold"
                />
                <span className="text-[8px] text-gray-600 shrink-0">mem</span>
              </div>
            </div>
            <div>
              <label className="text-[9px] text-gray-500 block mb-1">Storage Limit</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number" min={0.1} step={0.1}
                  value={local.storage_limit_mb || 10}
                  onChange={e => setLocal(p => ({ ...p, storage_limit_mb: parseFloat(e.target.value) || 0.1 }))}
                  className="w-full h-7 bg-white/[0.04] border border-white/8 rounded-lg px-2 text-[11px] text-white focus:border-purple-500/40 focus:outline-none"
                  data-testid="config-storage-limit"
                />
                <span className="text-[8px] text-gray-600 shrink-0">MB</span>
              </div>
            </div>
            <div>
              <label className="text-[9px] text-gray-500 block mb-1">Cooldown</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number" min={1}
                  value={local.cooldown_minutes || 60}
                  onChange={e => setLocal(p => ({ ...p, cooldown_minutes: parseInt(e.target.value) || 1 }))}
                  className="w-full h-7 bg-white/[0.04] border border-white/8 rounded-lg px-2 text-[11px] text-white focus:border-purple-500/40 focus:outline-none"
                  data-testid="config-cooldown"
                />
                <span className="text-[8px] text-gray-600 shrink-0">min</span>
              </div>
            </div>
            <div>
              <label className="text-[9px] text-gray-500 block mb-1">Compile Failures</label>
              <button
                onClick={() => setLocal(p => ({ ...p, compile_failure_alert: !p.compile_failure_alert }))}
                className={`h-7 w-full flex items-center justify-center gap-1.5 rounded-lg border text-[11px] transition-colors ${
                  local.compile_failure_alert
                    ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                    : 'bg-white/[0.02] border-white/8 text-gray-500'
                }`}
                data-testid="config-compile-failure"
              >
                {local.compile_failure_alert ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                {local.compile_failure_alert ? 'On' : 'Off'}
              </button>
            </div>
          </div>
        </div>

        {/* Delivery Channels */}
        <div>
          <p className="text-[9px] text-gray-600 uppercase tracking-wider mb-2">Delivery Channels</p>
          <div className="grid gap-3 lg:grid-cols-2">
            {/* Email */}
            <div className={`p-3 rounded-lg border transition-colors ${
              local.email_enabled ? 'bg-blue-500/[0.04] border-blue-500/15' : 'bg-white/[0.01] border-white/5'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Mail className={`w-3.5 h-3.5 ${local.email_enabled ? 'text-blue-400' : 'text-gray-600'}`} />
                  <span className={`text-[11px] font-medium ${local.email_enabled ? 'text-blue-300' : 'text-gray-500'}`}>Email</span>
                </div>
                <button
                  onClick={() => setLocal(p => ({ ...p, email_enabled: !p.email_enabled }))}
                  className={`px-2 py-0.5 rounded text-[9px] font-medium transition-colors ${
                    local.email_enabled
                      ? 'bg-blue-500/20 text-blue-300'
                      : 'bg-white/5 text-gray-500'
                  }`}
                  data-testid="config-email-toggle"
                >
                  {local.email_enabled ? 'ON' : 'OFF'}
                </button>
              </div>
              {local.email_enabled && (
                <input
                  type="email"
                  placeholder="operator@hotel.com"
                  value={local.alert_email || ''}
                  onChange={e => setLocal(p => ({ ...p, alert_email: e.target.value }))}
                  className="w-full h-7 bg-white/[0.04] border border-white/8 rounded-lg px-2 text-[11px] text-white placeholder:text-gray-600 focus:border-blue-500/40 focus:outline-none"
                  data-testid="config-alert-email"
                />
              )}
            </div>

            {/* Telegram */}
            <div className={`p-3 rounded-lg border transition-colors ${
              local.telegram_enabled ? 'bg-cyan-500/[0.04] border-cyan-500/15' : 'bg-white/[0.01] border-white/5'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Send className={`w-3.5 h-3.5 ${local.telegram_enabled ? 'text-cyan-400' : 'text-gray-600'}`} />
                  <span className={`text-[11px] font-medium ${local.telegram_enabled ? 'text-cyan-300' : 'text-gray-500'}`}>Telegram</span>
                </div>
                <button
                  onClick={() => setLocal(p => ({ ...p, telegram_enabled: !p.telegram_enabled }))}
                  className={`px-2 py-0.5 rounded text-[9px] font-medium transition-colors ${
                    local.telegram_enabled
                      ? 'bg-cyan-500/20 text-cyan-300'
                      : 'bg-white/5 text-gray-500'
                  }`}
                  data-testid="config-telegram-toggle"
                >
                  {local.telegram_enabled ? 'ON' : 'OFF'}
                </button>
              </div>
              {local.telegram_enabled && (
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Bot Token"
                    value={local.alert_telegram_bot_token || ''}
                    onChange={e => setLocal(p => ({ ...p, alert_telegram_bot_token: e.target.value }))}
                    className="w-full h-7 bg-white/[0.04] border border-white/8 rounded-lg px-2 text-[11px] text-white placeholder:text-gray-600 focus:border-cyan-500/40 focus:outline-none"
                    data-testid="config-telegram-token"
                  />
                  <input
                    type="text"
                    placeholder="Chat ID"
                    value={local.alert_telegram_chat_id || ''}
                    onChange={e => setLocal(p => ({ ...p, alert_telegram_chat_id: e.target.value }))}
                    className="w-full h-7 bg-white/[0.04] border border-white/8 rounded-lg px-2 text-[11px] text-white placeholder:text-gray-600 focus:border-cyan-500/40 focus:outline-none"
                    data-testid="config-telegram-chatid"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MemoryDashboardPage({ embedded = false }) {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertConfig, setAlertConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compiling, setCompiling] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingAlerts, setTestingAlerts] = useState(false);
  const [tab, setTab] = useState('overview');

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [dashRes, analyticsRes, alertsRes] = await Promise.all([
        fetch(`${API}/api/metaverse/bitsy/memory-dashboard`),
        fetch(`${API}/api/metaverse/bitsy/memory-analytics`),
        fetch(`${API}/api/metaverse/bitsy/memory-alerts`),
      ]);
      const d = await dashRes.json();
      setData(d);
      try {
        const a = await analyticsRes.json();
        setAnalytics(a);
      } catch { /* analytics optional */ }
      try {
        const al = await alertsRes.json();
        setAlerts(al.alerts || []);
        setAlertConfig(al.config || null);
      } catch { /* alerts optional */ }
    } catch {
      toast.error('Failed to load memory dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCompile = async (hotelId) => {
    setCompiling(hotelId);
    try {
      const res = await fetch(`${API}/api/metaverse/bitsy/compile/${hotelId}`, { method: 'POST' });
      const result = await res.json();
      if (result.success) {
        toast.success(`Compiled ${result.compiled} memories into MP4`);
        fetchData(true);
      } else {
        toast.error(result.error || 'Compilation failed');
      }
    } catch {
      toast.error('Compilation request failed');
    } finally {
      setCompiling(null);
    }
  };

  const handleCompileAll = async () => {
    if (!data?.hotels?.length) return;
    setCompiling('all');
    let totalCompiled = 0;
    for (const hotel of data.hotels) {
      try {
        const res = await fetch(`${API}/api/metaverse/bitsy/compile/${hotel.hotel_id}`, { method: 'POST' });
        const result = await res.json();
        if (result.success) totalCompiled += result.compiled;
      } catch { /* continue */ }
    }
    toast.success(`Compiled ${totalCompiled} memories across ${data.hotels.length} hotels`);
    setCompiling(null);
    fetchData(true);
  };

  const totals = data?.totals || {};
  const hotels = (data?.hotels || []).filter(h => h.hotel_id !== 'default');
  const alertedHotelIds = new Set(alerts.map(a => a.hotel_id));

  const handleSaveConfig = async (config) => {
    setSavingConfig(true);
    try {
      const res = await fetch(`${API}/api/metaverse/bitsy/memory-alerts/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      const result = await res.json();
      setAlertConfig(result.config);
      toast.success('Alert thresholds updated');
      fetchData(true);
    } catch {
      toast.error('Failed to save config');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTestAlerts = async () => {
    setTestingAlerts(true);
    try {
      const res = await fetch(`${API}/api/metaverse/bitsy/memory-alerts/test`, { method: 'POST' });
      const result = await res.json();
      if (result.success) {
        const channels = [];
        if (result.results?.telegram?.sent) channels.push('Telegram');
        if (result.results?.email?.status === 'sent' || result.results?.email?.id) channels.push('Email');
        if (channels.length > 0) {
          toast.success(`Test sent via ${channels.join(' & ')}`);
        } else {
          toast.info('Test queued — check configured channels');
        }
      } else {
        toast.error(result.message || 'Test delivery failed');
      }
    } catch {
      toast.error('Test request failed');
    } finally {
      setTestingAlerts(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className={embedded ? 'text-white' : 'min-h-screen bg-[#030712] text-white'} data-testid="memory-dashboard">
      {/* Header */}
      {!embedded ? (
      <div className="border-b border-white/5 bg-white/[0.01]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/platform-admin" className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 text-gray-400" />
            </Link>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-[#F7931A]/20 border border-purple-500/20 flex items-center justify-center">
                <Database className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <h1 className="text-base font-bold heading-font">Memvid Memory</h1>
                <p className="text-[10px] text-gray-500">MP4-Encoded AI Agent Memory</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Tab Toggle */}
            <div className="flex items-center bg-white/[0.03] rounded-lg border border-white/5 p-0.5">
              {[
                { id: 'overview', label: 'Overview', icon: Database },
                { id: 'analytics', label: 'Analytics', icon: BarChart3 },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-medium transition-all ${
                    tab === t.id
                      ? 'bg-purple-600/20 text-purple-300 border border-purple-500/20'
                      : 'text-gray-500 hover:text-gray-300 border border-transparent'
                  }`}
                  data-testid={`tab-${t.id}`}
                >
                  <t.icon className="w-3 h-3" />
                  {t.label}
                </button>
              ))}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="text-xs gap-1.5 h-8 border-white/10 text-gray-400 hover:bg-white/5"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {tab === 'overview' && (
              <Button
                size="sm"
                className="text-xs gap-1.5 h-8 bg-purple-600 hover:bg-purple-700 text-white"
                onClick={handleCompileAll}
                disabled={!!compiling}
                data-testid="compile-all-btn"
              >
                {compiling === 'all' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Compile All
              </Button>
            )}
          </div>
        </div>
      </div>
      ) : (
      <div className="px-4 lg:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-[#F7931A]/20 border border-purple-500/20 flex items-center justify-center">
            <Database className="w-4 h-4 text-purple-400" />
          </div>
          <div>
            <h1 className="text-base font-bold heading-font">Memvid Memory</h1>
            <p className="text-[10px] text-gray-500">MP4-Encoded AI Agent Memory</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-white/[0.03] rounded-lg border border-white/5 p-0.5">
            {[
              { id: 'overview', label: 'Overview', icon: Database },
              { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-medium transition-all ${
                  tab === t.id
                    ? 'bg-purple-600/20 text-purple-300 border border-purple-500/20'
                    : 'text-gray-500 hover:text-gray-300 border border-transparent'
                }`}
                data-testid={`tab-${t.id}`}
              >
                <t.icon className="w-3 h-3" />
                {t.label}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="outline"
            className="text-xs gap-1.5 h-8 border-white/10 text-gray-400 hover:bg-white/5"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {tab === 'overview' && (
            <Button
              size="sm"
              className="text-xs gap-1.5 h-8 bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleCompileAll}
              disabled={!!compiling}
              data-testid="compile-all-btn"
            >
              {compiling === 'all' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Compile All
            </Button>
          )}
        </div>
      </div>
      )}

      <div className={embedded ? 'px-4 lg:px-6 py-2 space-y-6' : 'max-w-5xl mx-auto px-4 py-6 space-y-6'}>
        {tab === 'overview' && (
          <>
            {/* Global Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatCard icon={Database} label="Hotels" value={totals.total_hotels || 0} color="text-purple-400" />
              <StatCard icon={Brain} label="Memories" value={totals.total_memories || 0} sub={`${totals.pending_compilation || 0} pending`} color="text-[#F7931A]" />
              <StatCard icon={CheckCircle2} label="Compiled" value={totals.compiled_memories || 0} color="text-emerald-400" />
              <StatCard icon={Users} label="Guests" value={totals.total_guests || 0} color="text-blue-400" />
              <StatCard icon={MessageSquare} label="Sessions" value={totals.total_sessions || 0} color="text-cyan-400" />
              <StatCard icon={HardDrive} label="Storage" value={formatBytes(totals.total_mp4_size_bytes)} color="text-purple-400" />
            </div>

            {/* Architecture info */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-purple-500/[0.04] border border-purple-500/10">
              <Film className="w-4 h-4 text-purple-400 shrink-0" />
              <p className="text-[11px] text-gray-400 leading-relaxed">
                <span className="text-purple-400 font-medium">Memvid</span> encodes guest memories as QR codes inside MP4 video frames, indexed by FAISS for sub-100ms semantic retrieval. Each hotel&apos;s memory is sovereign — a single portable file.
              </p>
            </div>

            {/* Auto-compile status */}
            {totals.auto_compile && (
              <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-emerald-500/[0.04] border border-emerald-500/10" data-testid="auto-compile-status">
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <span className="text-[10px] text-emerald-400 font-medium">Auto-Compile Active</span>
                </div>
                <span className="text-[10px] text-gray-500">Buffers are compiled to MP4 every {totals.auto_compile.interval_minutes} minutes automatically</span>
              </div>
            )}

            {/* Pipeline visualization */}
            <div className="flex items-center justify-center gap-2 py-2">
              {[
                { label: 'Guest Chat', icon: MessageSquare, color: 'text-blue-400 border-blue-500/20 bg-blue-500/5' },
                { label: 'MongoDB Buffer', icon: Clock, color: 'text-amber-400 border-amber-500/20 bg-amber-500/5' },
                { label: 'QR Encode', icon: Database, color: 'text-purple-400 border-purple-500/20 bg-purple-500/5' },
                { label: 'MP4 Video', icon: Film, color: 'text-[#F7931A] border-[#F7931A]/20 bg-[#F7931A]/5' },
                { label: 'FAISS Search', icon: Brain, color: 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5' },
              ].map((step, i, arr) => (
                <div key={step.label} className="flex items-center gap-2">
                  <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${step.color}`}>
                    <step.icon className="w-3 h-3" />
                    <span className="text-[9px] font-medium">{step.label}</span>
                  </div>
                  {i < arr.length - 1 && <span className="text-gray-700 text-xs">&rarr;</span>}
                </div>
              ))}
            </div>

            {/* Hotel Cards */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold heading-font text-gray-300">Hotels ({hotels.length})</h2>
                {alerts.length > 0 && (
                  <Badge variant="outline" className="text-[9px] border-amber-500/20 text-amber-400 bg-amber-500/5 gap-1" data-testid="alert-count-badge">
                    <AlertTriangle className="w-3 h-3" />
                    {alerts.length} alert{alerts.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              {hotels.length === 0 ? (
                <Card className="bg-white/[0.03] border-white/5">
                  <CardContent className="p-8 text-center">
                    <Film className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No hotel memories yet</p>
                    <p className="text-[10px] text-gray-600 mt-1">Start a conversation with Bitsy in a hotel metaverse to create memories</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 lg:grid-cols-2">
                  {hotels.map(hotel => (
                    <HotelMemoryCard
                      key={hotel.hotel_id}
                      hotel={hotel}
                      onCompile={handleCompile}
                      compiling={compiling === hotel.hotel_id || compiling === 'all'}
                      hasAlert={alertedHotelIds.has(hotel.hotel_id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {tab === 'analytics' && (
          <div className="space-y-6" data-testid="analytics-tab">
            {!analytics ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-5 h-5 animate-spin text-purple-400 mr-2" />
                <span className="text-sm text-gray-500">Loading analytics...</span>
              </div>
            ) : (
              <>
                <AnalyticsSummaryCards summary={analytics.summary} />

                {/* Active Alerts */}
                <div>
                  <h2 className="text-sm font-semibold heading-font text-gray-300 mb-3 flex items-center gap-2">
                    <Bell className="w-3.5 h-3.5 text-gray-400" />
                    Active Alerts
                    {alerts.length > 0 && (
                      <Badge variant="outline" className="text-[8px] h-4 border-amber-500/20 text-amber-400 bg-amber-500/5">
                        {alerts.length}
                      </Badge>
                    )}
                  </h2>
                  <ActiveAlerts alerts={alerts} />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <MemoryTrendChart data={analytics.daily_trend} />
                  <HotelVolumeChart data={analytics.hotel_volumes} />
                </div>

                <CompilationTimeline data={analytics.compilation_history} />

                {/* Alert Config */}
                <AlertConfigPanel config={alertConfig} onSave={handleSaveConfig} saving={savingConfig} onTest={handleTestAlerts} testing={testingAlerts} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
